import React, { useState, useEffect } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/Button Triangle.png";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import FilterIcon from "../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../assets/Icons/Left circle B.png";
import RightCircle from "../../assets/Icons/Right circle A.png";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosGetApi, axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import { format, parse } from "date-fns";
import { useSelector } from "react-redux";
import Dropdown from "../../components/Dropdown";
import { formatAmount, formatDate } from "../../helpers/helper";

const LedgerReportFilterModal = ({
  show,
  handleClose,
  setLedgerReportData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
  setCurrentPage,
}) => {
  const { user } = useSelector((state) => state.auth);

  // Helper function to get date 7 days ago
  const getLast7DaysDate = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const year = sevenDaysAgo.getFullYear();
    const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
    const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to get today's date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Set default date range (last 7 days)
  const getDefaultFromDate = () => {
    // If filterData already has fromDate, use that (for when modal reopens with existing filters)
    if (filterData?.fromDate) {
      return filterData.fromDate;
    }
    // Otherwise, set default to 7 days ago
    return getLast7DaysDate();
  };

  const getDefaultToDate = () => {
    // If filterData already has toDate, use that (for when modal reopens with existing filters)
    if (filterData?.toDate) {
      return filterData.toDate;
    }
    // Otherwise, set default to today
    return getTodayDate();
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      walletNumber: filterData.walletNumber || "",
      walletUserName: filterData.walletUserName || "",
      batchNumber: filterData.batchNumber || "",
      fromDate: getDefaultFromDate(),
      toDate: getDefaultToDate(),
      createdBy: filterData.createdBy || "",
      drCr: filterData.drCr || "",
      paymentMode: filterData.paymentMode || "",
      ewaybillNumber: filterData.ewaybillNumber || "",
    },
    mode: "onChange",
  });

  // Watch all form values to check if a filter has a value
  const watchedValues = watch();

  // Function to check if a tab has an active filter value
  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "Wallet Number":
        return (
          watchedValues.walletNumber && watchedValues.walletNumber.trim() !== ""
        );
      case "Batch Number":
        return (
          watchedValues.batchNumber && watchedValues.batchNumber.trim() !== ""
        );
      case "Wallet Name":
        return (
          watchedValues.walletUserName &&
          watchedValues.walletUserName.trim() !== ""
        );
      case "Transaction Date":
        return (
          (watchedValues.fromDate &&
            watchedValues.fromDate !== getDefaultFromDate()) ||
          (watchedValues.toDate && watchedValues.toDate !== getDefaultToDate())
        );
      case "Dr/Cr":
        return watchedValues.drCr && watchedValues.drCr !== "";
      case "Payment Mode":
        return watchedValues.paymentMode && watchedValues.paymentMode !== "";
      case "Created By":
        return watchedValues.createdBy && watchedValues.createdBy.trim() !== "";
      case "Ewaybill Number":
        return (
          watchedValues.ewaybillNumber &&
          watchedValues.ewaybillNumber.trim() !== ""
        );
      default:
        return false;
    }
  };

  const [selectedTab, setSelectedTab] = useState(
    user?.data?.userProfile === "CUSTOMER" ? "Batch Number" : "Wallet Number",
  );
  const [loading, setLoading] = useState(false);
  const loggedInUserName = sessionStorage.getItem("username");
  const [ledgerUsers, setLedgerUsers] = useState([]);

  // Options for Dr/Cr dropdown
  const drCrOptions = [
    { label: "Debit (DR)", value: "DR" },
    { label: "Credit (CR)", value: "CR" },
  ];

  // Options for Payment Mode dropdown
  const paymentModeOptions = [
    { label: "Wallet", value: "WALLET" },
    { label: "UPI", value: "UPI" },
    { label: "Debit Card (DCI)", value: "DCI" },
    { label: "Credit Card (CCI)", value: "CCI" },
  ];

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  // Get available tabs based on user profile
  const getAvailableTabs = () => {
    const allTabs = [
      "Wallet Number",
      "Batch Number",
      "Wallet Name",
      "Transaction Date",
      "Dr/Cr",
      "Payment Mode",
      "Ewaybill Number",
    ];

    if (user?.data?.userProfile === "CUSTOMER") {
      // Hide Wallet Number and Created By for CUSTOMER
      return allTabs.filter(
        (tab) =>
          tab !== "Wallet Number" &&
          tab !== "Created By" &&
          tab !== "Wallet Name",
      );
    }

    return allTabs;
  };

  const fetchLedgerReportData = async (page, formData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        ...formData,
      };

      if (formData?.walletNumber?.trim()) {
        requestBody.walletNumber = formData.walletNumber.trim();
      }

      if (formData?.fromDate) {
        requestBody.fromDate = formatDate(formData.fromDate);
      }

      if (formData?.toDate) {
        requestBody.toDate = formatDate(formData.toDate);
      }

      if (formData?.createdBy?.trim()) {
        requestBody.createdBy = formData.createdBy.trim();
      }

      // Add Dr/Cr filter if selected (value should be "DR" or "CR")
      if (formData?.drCr) {
        requestBody.drCr = formData.drCr;
      }

      // Add Payment Mode filter if selected (value should be "WALLET", "UPI", "DCI", or "CCI")
      if (formData?.paymentMode) {
        requestBody.paymentMode = formData.paymentMode;
      }

      // Add Ewaybill Number filter if selected
      if (formData?.ewaybillNumber?.trim()) {
        requestBody.ewaybillNumber = formData.ewaybillNumber.trim();
      }

      const response = await axiosPostApi(
        `${apiList.FETCH_LEDGER_REPORT_DATA}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails || null);

        const modifiedData = modifyStructure(response?.data?.data || []);
        setLedgerReportData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setLedgerReportData([]);
        setNoData(true);
      }
    } catch (error) {
      console.error("API Error:", error);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
      setLedgerReportData([]);
      setNoData(true);
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    const modified = response?.map((item, index) => {
      const txnDateTimeParsed = item?.transactionDateTime
        ? parse(item.transactionDateTime, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const createdDateParsed = item?.createdDate
        ? parse(item.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const modifiedDateParsed = item?.modifiedDate
        ? parse(item.modifiedDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const authDateParsed = item?.authDate
        ? parse(item.authDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const formattedAmount =
        item?.amount !== undefined ? Number(item.amount).toFixed(2) : "";

      const formattedNarration = item?.narration
        ? item.narration.length > 40
          ? `${item.narration.substring(0, 40)}...`
          : item.narration
        : "";

      return {
        ...item,

        transactionDateTime: txnDateTimeParsed
          ? [
              format(txnDateTimeParsed, "dd-MM-yyyy"),
              <br key={`txn-${index}`} />,
              format(txnDateTimeParsed, "HH:mm:ss"),
            ]
          : "",

        createdDate: createdDateParsed
          ? [
              format(createdDateParsed, "dd-MM-yyyy"),
              <br key={`created-${index}`} />,
              format(createdDateParsed, "HH:mm:ss"),
            ]
          : "",

        amount: (
          <div
            style={{
              width: "82px",
              textAlign: "end",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.amount)}
          >
            {formatAmount(item.amount)}
          </div>
        ),

        openingBalance: (
          <div
            style={{
              width: "111px",
              textAlign: "end",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.openingBalance)}
          >
            {formatAmount(item.openingBalance)}
          </div>
        ),

        closingBalance: (
          <div
            style={{
              textAlign: "end",
              width: "102px",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.closingBalance)}
          >
            {formatAmount(item.closingBalance)}
          </div>
        ),

        sequenceNumber: (
          <div
            style={{
              width: "51px",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
            title={item.sequenceNumber}
          >
            {item.sequenceNumber}
          </div>
        ),

        modifiedDate: modifiedDateParsed
          ? [
              format(modifiedDateParsed, "dd-MM-yyyy"),
              <br key={`modified-${index}`} />,
              format(modifiedDateParsed, "HH:mm:ss"),
            ]
          : "",

        authDate: authDateParsed
          ? [
              format(authDateParsed, "dd-MM-yyyy"),
              <br key={`auth-${index}`} />,
              format(authDateParsed, "HH:mm:ss"),
            ]
          : "",

        refund:
          item?.refund === true
            ? "True"
            : item?.refund === false
            ? "False"
            : "",

        // amount: formattedAmount,

        narration: item?.narration || "",

        walletNumber: item?.walletNumber || "",
      };
    });

    return modified;
  };

  useEffect(() => {
    fetchLedgerUsers();
  }, []);

  // Update form values when filterData changes (e.g., when modal reopens with existing filters)
  useEffect(() => {
    if (show) {
      setValue("fromDate", getDefaultFromDate());
      setValue("toDate", getDefaultToDate());
    }
  }, [show, filterData?.fromDate, filterData?.toDate, setValue]);

  const fetchLedgerUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosGetApi(apiList.FETCH_GENERAL_LEDGER_DATA);

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setLedgerUsers(response?.data?.data);
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    const formattedData = {
      walletNumber: data.walletNumber || "",
      walletUserName: data.walletUserName || "",
      batchNumber: data.batchNumber || "",
      fromDate: data.fromDate || "",
      toDate: data.toDate || "",
      createdBy: data.createdBy || "",
      drCr: data.drCr || "",
      paymentMode: data.paymentMode || "",
      ewaybillNumber: data.ewaybillNumber || "",
    };

    setFilterData(formattedData);
    fetchLedgerReportData(1, formattedData);
    setCurrentPage(1);
  };

  const onReset = () => {
    // Reset to default last 7 days range instead of clearing dates
    const defaultFromDate = getLast7DaysDate();
    const defaultToDate = getTodayDate();

    reset({
      walletNumber: "",
      walletUserName: "",
      batchNumber: "",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      createdBy: "",
      drCr: "",
      paymentMode: "",
      ewaybillNumber: "",
    });

    const defaultFilterData = {
      walletNumber: "",
      walletUserName: "",
      batchNumber: "",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      createdBy: "",
      drCr: "",
      paymentMode: "",
      ewaybillNumber: "",
    };

    setFilterData(defaultFilterData);
    // Removed: handleReset() and fetchLedgerReportData() and setCurrentPage()
    // This keeps the modal open and only clears the selected filter data
  };

  // Reset selected tab when user profile changes or modal opens
  useEffect(() => {
    if (show) {
      setSelectedTab(
        user?.data?.userProfile === "CUSTOMER"
          ? "Transaction Date"
          : "Wallet Number",
      );
    }
  }, [show, user?.data?.userProfile]);

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="rightImg"
                className="absolute right-0 top-0 w-[110px] h-[100px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title font-medium">
                    Filters
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="close icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {getAvailableTabs().map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {selectedTab === "Wallet Number" && (
                        <Controller
                          name="walletNumber"
                          control={control}
                          rules={{
                            pattern: {
                              value: /^[A-Za-z0-9_]+$/,
                              message: "Wallet number must contain only digits",
                            },
                            maxLength: {
                              value: 20,
                              message: "Wallet number cannot exceed 20 digits",
                            },
                          }}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Wallet Number</label>
                              <div className="dateInputWrapper">
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Wallet Number"
                                  className="dateInput"
                                  maxLength={20}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^A-Za-z0-9_]/g,
                                      "",
                                    );
                                    field.onChange(val);
                                  }}
                                />
                                {errors.walletNumber && (
                                  <div className="error-text">
                                    {errors.walletNumber.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Batch Number" && (
                        <Controller
                          name="batchNumber"
                          control={control}
                          rules={{
                            pattern: {
                              value: /^[A-Za-z0-9-]+$/,
                              message:
                                "Batch number can contain letters, numbers and hyphen (-)",
                            },
                          }}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Batch Number</label>
                              <div className="dateInputWrapper">
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Batch Number"
                                  className="dateInput"
                                  maxLength={20}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^A-Za-z0-9-]/g,
                                      "",
                                    );
                                    field.onChange(val);
                                  }}
                                />
                                {errors.batchNumber && (
                                  <div className="error-text">
                                    {errors.batchNumber.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Wallet Name" && (
                        <Controller
                          name="walletUserName"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <Dropdown
                                label="Wallet Name"
                                options={ledgerUsers}
                                value={field.value}
                                labelKey="walletUserName"
                                valueKey="walletUserName"
                                placeholder="Select Wallet Name"
                                onChange={(item) => {
                                  field.onChange(item.walletUserName);

                                  setFilterData((prev) => ({
                                    ...prev,
                                    walletUserName: item.walletUserName,
                                  }));
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Transaction Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {/* Dr/Cr Filter - Values: "DR" or "CR" */}
                      {selectedTab === "Dr/Cr" && (
                        <Controller
                          name="drCr"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <Dropdown
                                label="Dr/Cr (Debit/Credit)"
                                options={drCrOptions}
                                value={field.value}
                                labelKey="label"
                                valueKey="value"
                                placeholder="Select Option"
                                onChange={(item) => {
                                  field.onChange(item.value);
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {/* Payment Mode Filter - Values: "WALLET", "UPI", "DCI", or "CCI" */}
                      {selectedTab === "Payment Mode" && (
                        <Controller
                          name="paymentMode"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <Dropdown
                                label="Payment Mode"
                                options={paymentModeOptions}
                                value={field.value}
                                labelKey="label"
                                valueKey="value"
                                placeholder="Select Payment Mode"
                                onChange={(item) => {
                                  field.onChange(item.value);
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Created By" && (
                        <Controller
                          name="createdBy"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Created By</label>
                              <div className="dateInputWrapper">
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Created By"
                                  className="dateInput"
                                  maxLength={50}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    field.onChange(val);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Ewaybill Number" &&
                      // user?.data?.userProfile === "OPERATION" && (
                        <Controller
                          name="ewaybillNumber"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <div className="dateInputWrapper">
                                <label className="dateLabel">
                                  Ewaybill Number
                                </label>
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Ewaybill Number"
                                  className="dateInput"
                                  onChange={(e) => {
                                    field.onChange(e);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      ewaybillNumber: e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      // )
                      }
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back !rounded-lg w-[133px]"
                      onClick={onReset}
                      disabled={loading}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit !rounded-lg w-[133px]"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LedgerReportFilterModal;
